import Card from 'components/Card/Card';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import SignupAgents from './SignupAgents';
import BulkOnboardingUpload from './BulkOnboardingUpload';
import FormPreferencesContent from './FormPreferencesContent';
import InvitesDisabledMessage from './InvitesDisabledMessage';
import { useSelector } from 'react-redux';
import { getCompany } from 'helpers/getCompanyVariables';
import { areOnboardingInvitesDisabled } from 'helpers/viewing-rules';
import { resolveAgencyId } from 'helpers/agencyManagement/agency-id';

const company = getCompany();
const isOneill = company === 'oneill';

const INITIAL_VALUES = {
  Hide_ACA: false,
  Hide_ACA_Other: false,
  Hide_Group: false,
  Hide_Life: false,
  Hide_Annuity: false,
  Hide_Long: false,
  Hide_Disability: false,
  Hide_Medicare: false,
  Hide_OffMarket: false,
  Hide_Ancillary: false,
  Healthsherpa_Agency_Code: ''
};

function buildPreferencesFromAgencyData(source = {}) {
  return {
    Hide_ACA: !!source.Hide_ACA,
    Hide_ACA_Other: !!source.Hide_ACA_Other,
    Hide_Group: !!source.Hide_Group,
    Hide_Life: !!source.Hide_Life,
    Hide_Annuity: !!source.Hide_Annuity,
    Hide_Long: !!source.Hide_Long_term_Health,
    Hide_Disability: !!source.Hide_Disability,
    Hide_Medicare: !!source.Hide_Medicare,
    Hide_OffMarket: !!source.Hide_Off_Market,
    Hide_Ancillary: !!(
      source.Hide_Ancillary ?? source.Hide_O_Neill_Insurance_Email_Creation
    ),
    Healthsherpa_Agency_Code: source.Healthsherpa_Agency_Code || ''
  };
}

export default function OnboardingFormManagement({
  UpdateAgencyFormPreferences
}) {
  const agencyInformation = useSelector(
    state => state.agencyManagement.agencyData.agencyInformation
  );

  const userAgencyData = useSelector(state => state.user.zohoData.agencyData);

  const invitesDisabled = areOnboardingInvitesDisabled(userAgencyData);

  const agencyManagement = useSelector(state => state.agencyManagement);
  const zohoData = useSelector(state => state.user.zohoData);

  const [preferencesData, setPreferencesData] = useState(INITIAL_VALUES);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const prevData = useRef(INITIAL_VALUES);

  // true when the given prefs match the last saved snapshot (save disabled).
  const arePreferencesUnchanged = useCallback(next => {
    return !Object.keys(next).some(key => prevData.current[key] !== next[key]);
  }, []);

  useEffect(() => {
    const agencyId = resolveAgencyId(agencyInformation);
    const userAgencyId = zohoData?.Account_Name?.id;
    const isOwnAgency =
      !agencyId || !userAgencyId || String(agencyId) === String(userAgencyId);

    if (!isOwnAgency && !agencyId) return;

    const source = isOwnAgency
      ? userAgencyData || agencyInformation
      : agencyInformation;
    const data = buildPreferencesFromAgencyData(source);

    setPreferencesData(data);
    prevData.current = data;
    setIsSaveDisabled(true);
  }, [agencyInformation, userAgencyData, zohoData?.Account_Name?.id]);

  // stable so the memoized CheckBoxItem only re-renders on `checked` changes.
  const handleChangePreference = useCallback(
    (event, preference) => {
      setPreferencesData(prev => {
        const next = { ...prev, [preference]: event.target.checked };
        setIsSaveDisabled(arePreferencesUnchanged(next));
        return next;
      });
    },
    [arePreferencesUnchanged]
  );

  const handleChangeAgencyCodePreference = useCallback(
    (event, preference) => {
      setPreferencesData(prev => {
        const next = { ...prev, [preference]: event.target.value };
        setIsSaveDisabled(arePreferencesUnchanged(next));
        return next;
      });
    },
    [arePreferencesUnchanged]
  );

  const handleSavePreferences = async () => {
    const actualUpdates = {};

    if (isOneill) {
      actualUpdates.Hide_OffMarket = preferencesData.Hide_OffMarket;
      actualUpdates.Healthsherpa_Agency_Code =
        preferencesData.Healthsherpa_Agency_Code;
      actualUpdates.Hide_Ancillary = preferencesData.Hide_Ancillary;
    } else {
      Object.keys(preferencesData).forEach(key => {
        actualUpdates[key] = preferencesData[key];
      });
      delete actualUpdates.Hide_Ancillary;
    }

    await UpdateAgencyFormPreferences(actualUpdates);

    prevData.current = preferencesData;
    setIsSaveDisabled(true);
  };

  const formPreferencesProps = {
    preferencesData,
    handleChangePreference,
    handleChangeAgencyCodePreference,
    handleSavePreferences,
    isSaveDisabled,
    isUpdatingPreferences: agencyManagement.isUpdatingPreferences
  };

  if (isOneill) {
    return (
      <>
        <Card title='Agency Onboarding Invites' titleType='primary'>
          <div data-tour='settings-invite'>
            {invitesDisabled ? (
              <InvitesDisabledMessage agencyInformation={agencyInformation} />
            ) : (
              <>
                <SignupAgents
                  zohoData={zohoData}
                  Healthsherpa_Agency_Code={
                    preferencesData.Healthsherpa_Agency_Code
                  }
                />
                <BulkOnboardingUpload />
              </>
            )}
          </div>
        </Card>
        <Card title='Agency Settings' titleType='primary'>
          <div data-tour='settings-onboarding'>
            <FormPreferencesContent {...formPreferencesProps} />
          </div>
        </Card>
      </>
    );
  }

  return (
    <Card>
      {!invitesDisabled && <h4>Step 1: Form Preferences</h4>}
      <FormPreferencesContent {...formPreferencesProps} />
      {!invitesDisabled && (
        <>
          <br />
          <div data-tour='settings-invite'>
            <SignupAgents
              zohoData={zohoData}
              Healthsherpa_Agency_Code={
                preferencesData.Healthsherpa_Agency_Code
              }
            />
          </div>
        </>
      )}

      {/* explicitly check if company isn't Sunshine because invites are disabled by default for Sunshine */}
      {invitesDisabled && company !== 'sunshine' && (
        <InvitesDisabledMessage agencyInformation={agencyInformation} />
      )}
    </Card>
  );
}

OnboardingFormManagement.propTypes = {
  UpdateAgencyFormPreferences: PropTypes.func
};
